import React, { useState, useEffect, useRef } from "react";
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";
import "./LanguageSelector.css";

function LanguageSelector({ ...props }) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  const languages = [
    { code: "en", label: "English", country: "US" },
    { code: "sq", label: "Albanian", country: "AL" },
    { code: "am", label: "Amharic", country: "ET" },
    { code: "ar", label: "Arabic", country: "SA" },
    { code: "az", label: "Azerbaijani", country: "AZ" },
    { code: "eu", label: "Basque", country: "ES" },
    { code: "bn", label: "Bengali", country: "IN" },
    { code: "bs", label: "Bosnian", country: "BA" },
    { code: "bg", label: "Bulgarian", country: "BG" },
    { code: "my", label: "Burmese", country: "MM" },
    { code: "ca", label: "Catalan", country: "ES" },
    { code: "zh", label: "Chinese", country: "CN" },
    { code: "hr", label: "Croatian", country: "HR" },
    { code: "cs", label: "Czech", country: "CZ" },
    { code: "da", label: "Danish", country: "DK" },
    { code: "nl", label: "Dutch", country: "NL" },
    { code: "et", label: "Estonian", country: "EE" },
    { code: "fi", label: "Finnish", country: "FI" },
    { code: "fr", label: "French", country: "FR" },
    { code: "gl", label: "Galician", country: "ES" },
    { code: "ka", label: "Georgian", country: "GE" },
    { code: "de", label: "German", country: "DE" },
    { code: "el", label: "Greek", country: "GR" },
    { code: "gu", label: "Gujarati", country: "IN" },
    { code: "he", label: "Hebrew", country: "IL" },
    { code: "hi", label: "Hindi", country: "IN" },
    { code: "hu", label: "Hungarian", country: "HU" },
    { code: "id", label: "Indonesian", country: "ID" },
    { code: "it", label: "Italian", country: "IT" },
    { code: "ja", label: "Japanese", country: "JP" },
    { code: "km", label: "Khmer", country: "KH" },
    { code: "ko", label: "Korean", country: "KR" },
    { code: "lo", label: "Lao", country: "LA" },
    { code: "lv", label: "Latvian", country: "LV" },
    { code: "lt", label: "Lithuanian", country: "LT" },
    { code: "mk", label: "Macedonian", country: "MK" },
    { code: "ms", label: "Malay", country: "MY" },
    { code: "ml", label: "Malayalam", country: "IN" },
    { code: "mn", label: "Mongolian", country: "MN" },
    { code: "fa", label: "Persian", country: "IR" },
    { code: "pl", label: "Polish", country: "PL" },
    { code: "pt", label: "Portuguese", country: "PT" },
    { code: "ro", label: "Romanian", country: "RO" },
    { code: "ru", label: "Russian", country: "RU" },
    { code: "sr", label: "Serbian", country: "RS" },
    { code: "sk", label: "Slovak", country: "SK" },
    { code: "sl", label: "Slovenian", country: "SI" },
    { code: "es", label: "Spanish", country: "ES" },
    { code: "sv", label: "Swedish", country: "SE" },
    { code: "tl", label: "Tagalog", country: "PH" },
    { code: "te", label: "Telugu", country: "IN" },
    { code: "th", label: "Thai", country: "TH" },
    { code: "tr", label: "Turkish", country: "TR" },
    { code: "uk", label: "Ukrainian", country: "UA" },
    { code: "vi", label: "Vietnamese", country: "VN" },
  ];

  const currentLanguage =
    languages.find((l) => l.code === i18n.language) || languages[0];

  const handleSelectLanguage = (code) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
    setSearchQuery("");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredLanguages = languages.filter((lang) =>
    lang.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="custom-lang-selector" ref={dropdownRef} {...props}>
      <button
        type="button"
        className={`lang-trigger-btn ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <ReactCountryFlag
          countryCode={currentLanguage.country}
          svg
          className="country-flag"
        />
        <span className="lang-current-label">{currentLanguage.label}</span>
        <svg
          className={`lang-chevron ${isOpen ? "open" : ""}`}
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1L5 5L9 1"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="lang-dropdown-menu">
          <div className="lang-search-wrapper">
            <svg
              className="lang-search-icon"
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                stroke="#94A3B8"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 14L11.1 11.1"
                stroke="#94A3B8"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              className="lang-search-input"
              placeholder="Search language..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>

          <div className="lang-options-list">
            {filteredLanguages.length > 0 ? (
              filteredLanguages.map(({ code, label, country }) => {
                const isSelected = code === currentLanguage.code;
                return (
                  <div
                    key={code}
                    className={`lang-option-item ${isSelected ? "selected" : ""}`}
                    onClick={() => handleSelectLanguage(code)}
                  >
                    <div className="lang-option-left">
                      <ReactCountryFlag
                        countryCode={country}
                        svg
                        className="option-country-flag"
                      />
                      <span className="lang-option-name">{label}</span>
                    </div>
                    {isSelected && (
                      <svg
                        className="lang-check-icon"
                        width="14"
                        height="10"
                        viewBox="0 0 14 10"
                        fill="none"
                      >
                        <path
                          d="M1.5 5L5 8.5L12.5 1"
                          stroke="#094C80"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="lang-no-results">No languages found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LanguageSelector;
